import React, { useState, useEffect, useCallback } from "react";
import { getToken } from "../../utils/tokenStorage";
import {
  Newspaper, Sparkles, RefreshCw, Eye, Pencil, Trash2,
  Send, Undo2, X, ExternalLink, Loader2,
} from "lucide-react";

const blogApi = (path, opts = {}) => {
  const token = getToken("master_token");
  return fetch(`/api/blog${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  }).then(async (r) => {
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
    return body;
  });
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function MasterBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);   // full post being edited
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await blogApi("/admin/all?limit=100"); setPosts(r.posts || []); }
    catch (e) { setMsg("Load failed: " + e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true); setMsg("");
    try {
      const r = await blogApi("/generate", { method: "POST", body: JSON.stringify({ status: "draft" }) });
      setMsg(`Draft created: "${r.post?.title}"`);
      await load();
    } catch (e) { setMsg("Generate failed: " + e.message); }
    finally { setGenerating(false); }
  };

  const setStatus = async (id, status) => {
    try { await blogApi(`/admin/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }); await load(); }
    catch (e) { setMsg(e.message); }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try { await blogApi(`/admin/${id}`, { method: "DELETE" }); await load(); }
    catch (e) { setMsg(e.message); }
  };

  const openEdit = async (id) => {
    try {
      const p = await blogApi(`/admin/${id}`);
      setEditing(p);
      setForm({ title: p.title, excerpt: p.excerpt || "", tags: (p.tags || []).join(", "), body_html: p.body_html, status: p.status });
    } catch (e) { setMsg(e.message); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await blogApi(`/admin/${editing.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: form.title,
          excerpt: form.excerpt,
          tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
          body_html: form.body_html,
          status: form.status,
        }),
      });
      setEditing(null); await load();
    } catch (e) { setMsg("Save failed: " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Newspaper size={20} style={{ color: "var(--au-accent-ink)" }} />
          <h2 style={{ margin: 0, fontSize: 18, color: "var(--au-text)" }}>Blog Posts</h2>
          <span style={{ fontSize: 12, color: "var(--au-muted)" }}>{posts.length} total</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="au-pill au-pill-glass au-pill-sm" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="au-pill au-pill-primary au-pill-sm" onClick={generate} disabled={generating}>
            {generating ? <Loader2 size={14} className="au-spin" /> : <Sparkles size={14} />}
            {generating ? "Generating…" : "Generate Draft"}
          </button>
        </div>
      </div>

      {msg && (
        <div className="au-glass au-pad" style={{ marginBottom: 14, fontSize: 13, color: "var(--au-text-2)", display: "flex", justifyContent: "space-between", gap: 10 }}>
          <span>{msg}</span>
          <X size={16} style={{ cursor: "pointer", flex: "none" }} onClick={() => setMsg("")} />
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--au-muted)" }}>Loading…</div>
      ) : posts.length === 0 ? (
        <div className="au-glass au-pad" style={{ textAlign: "center", color: "var(--au-text-2)", padding: 40 }}>
          No posts yet. Click <strong>Generate Draft</strong> to create one from Reddit + AI.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {posts.map((p) => (
            <div key={p.id} className="au-glass au-pad" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              {p.hero_image && (
                <img src={p.hero_image} alt="" style={{ width: 64, height: 48, objectFit: "cover", borderRadius: 8, flex: "none" }} />
              )}
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "2px 8px", borderRadius: 20,
                    color: p.status === "published" ? "var(--au-success)" : "var(--au-warn)",
                    background: p.status === "published" ? "rgba(31,208,163,0.12)" : "rgba(212,160,23,0.12)",
                  }}>{(p.status || "").toUpperCase()}</span>
                  <span style={{ fontSize: 11, color: "var(--au-muted)" }}>{fmtDate(p.published_at)}</span>
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--au-text)" }}>{p.title}</div>
                <div style={{ fontSize: 12, color: "var(--au-text-2)", marginTop: 2 }}>{p.excerpt}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flex: "none" }}>
                <a href={`https://aegis.getkhojo.com/blog/${p.slug}`} target="_blank" rel="noopener noreferrer"
                   className="au-pill au-pill-glass au-pill-sm" title="View live"><Eye size={14} /></a>
                <button className="au-pill au-pill-glass au-pill-sm" onClick={() => openEdit(p.id)} title="Review / Edit"><Pencil size={14} /></button>
                {p.status === "published" ? (
                  <button className="au-pill au-pill-glass au-pill-sm" onClick={() => setStatus(p.id, "draft")} title="Unpublish"><Undo2 size={14} /></button>
                ) : (
                  <button className="au-pill au-pill-primary au-pill-sm" onClick={() => setStatus(p.id, "published")} title="Publish"><Send size={14} /></button>
                )}
                <button className="au-pill au-pill-glass au-pill-sm" onClick={() => del(p.id, p.title)} title="Delete"
                        style={{ color: "var(--au-error)" }}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit / Review modal */}
      {editing && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 4000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflowY: "auto" }}
             onMouseDown={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
          <div style={{ background: "var(--au-bg-elevated)", border: "1px solid var(--au-border)", borderRadius: 16, width: "min(900px, 100%)", padding: 24, margin: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, color: "var(--au-text)" }}>Review / Edit Post</h3>
              <X size={20} style={{ cursor: "pointer", color: "var(--au-text-2)" }} onClick={() => setEditing(null)} />
            </div>
            {(editing.sources || []).length > 0 && (
              <div style={{ fontSize: 12, color: "var(--au-muted)", marginBottom: 14 }}>
                Source: <a href={editing.sources[0].url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--au-accent-ink)" }}>
                  r/{editing.sources[0].subreddit} <ExternalLink size={11} style={{ display: "inline" }} />
                </a>
              </div>
            )}
            <Field label="Title">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} style={inp} />
            </Field>
            <Field label="Excerpt">
              <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} style={inp} />
            </Field>
            <Field label="Tags (comma-separated)">
              <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} style={inp} />
            </Field>
            <Field label="Body (HTML)">
              <textarea value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} rows={14}
                        style={{ ...inp, fontFamily: "monospace", fontSize: 12 }} />
            </Field>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, gap: 10 }}>
              <label style={{ fontSize: 13, color: "var(--au-text-2)", display: "flex", alignItems: "center", gap: 8 }}>
                Status:
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} style={{ ...inp, width: "auto", padding: "6px 10px" }}>
                  <option value="draft">draft</option>
                  <option value="published">published</option>
                </select>
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="au-pill au-pill-glass au-pill-sm" onClick={() => setEditing(null)}>Cancel</button>
                <button className="au-pill au-pill-primary au-pill-sm" onClick={save} disabled={saving}>
                  {saving ? <Loader2 size={14} className="au-spin" /> : null} Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = {
  width: "100%", background: "var(--au-glass)", border: "1px solid var(--au-border)",
  borderRadius: 8, padding: "9px 12px", color: "var(--au-text)", fontSize: 13, outline: "none",
  boxSizing: "border-box", fontFamily: "inherit",
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, color: "var(--au-muted)", marginBottom: 6 }}>{label.toUpperCase()}</div>
      {children}
    </div>
  );
}
